import { useEffect, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import Pagination from "../common/pagination";
import useDebouncedSearch from "../shared/hooks/useDebouncedSearch";
import { getStores } from "../../services/storeService";

const PAGE_SIZE = 5;

export default function StorePickerModal({ isOpen, onClose, onSelect }) {
  const { inputValue, debouncedValue, handleChange, reset } = useDebouncedSearch();
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    getStores(currentPage, debouncedValue, "", "", PAGE_SIZE)
      .then(({ data }) => {
        if (cancelled) return;
        setStores(data.results ?? []);
        setCount(data.count ?? 0);
      })
      .catch(() => {
        if (cancelled) return;
        setStores([]);
        setCount(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedValue, isOpen, currentPage]);

  const handleSearchChange = (value) => {
    setCurrentPage(1);
    handleChange(value);
  };

  const handleClose = () => {
    reset();
    setSelected(null);
    setCurrentPage(1);
    setCount(0);
    onClose();
  };

  const handleConfirm = () => {
    if (!selected) {
      return;
    }
    onSelect({ location: selected.location, name: selected.name });
    reset();
    setSelected(null);
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" contentClassName="bg-modal">
      <ModalHeader toggle={handleClose}>Pick a store</ModalHeader>
      <ModalBody>
        <div className="d-flex align-items-center gap-2 mb-3">
          <Input
            placeholder="Search by location or name…"
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoFocus
          />
          <Pagination
            itemsCount={count}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            label="stores"
          />
        </div>
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner color="primary" />
          </div>
        ) : stores.length === 0 ? (
          <div className="text-center py-3">No stores found. Refine your search.</div>
        ) : (
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th />
                <th>Location</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((store) => (
                <tr key={store.location} role="button" style={{ cursor: "pointer" }} onClick={() => setSelected(store)}>
                  <td>{selected?.location === store.location ? <i className="fas fa-check text-success" /> : null}</td>
                  <td>{store.location}</td>
                  <td>{store.name}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={!selected}>
          Select
        </Button>
      </ModalFooter>
    </Modal>
  );
}
